import type { FieldDefinition, ValidationIssue } from '@inside-us-aos/domain';

import { TextInput } from '../TextInput';

import styles from './NameComposite.module.css';

/**
 * The catalog stores legal-name as three separate `text` fields
 * (`*.name.legal.first`, `.middle`, `.last`) rather than a single
 * `valueType: 'name'` field — see `packages/domain/src/fields/aosV1.ts`.
 *
 * `NameComposite` renders the three sub-fields as one widget so the
 * UI lays them out side-by-side and the parent (the demo / a future
 * `FieldRenderer`) hooks each onto the engine through one render pass.
 *
 * Each sub-field still emits its own `onChange` — the engine writes
 * three separate answers, one per field. The composite is a layout
 * primitive, not an aggregator.
 */
export interface NameCompositeProps {
  /** Section heading rendered above the three sub-fields. */
  legend: string;
  firstNameField: FieldDefinition;
  middleNameField: FieldDefinition;
  lastNameField: FieldDefinition;
  values: {
    first: string;
    middle: string;
    last: string;
  };
  issuesByFieldId: ReadonlyMap<string, readonly ValidationIssue[]>;
  onChange: (sub: 'first' | 'middle' | 'last', next: string) => void;
  /**
   * Set of field-ids the user has interacted with. The composite
   * passes `touched={touchedFieldIds.has(field.id)}` through to each
   * sub-field. When omitted, every sub-field is treated as touched
   * (preserves the pre-Phase-7b default of "always show issues").
   */
  touchedFieldIds?: ReadonlySet<string>;
  /**
   * Fires when focus leaves any of the three sub-fields. The caller
   * receives the sub-key so it can mark the right field touched.
   */
  onBlur?: (sub: 'first' | 'middle' | 'last') => void;
}

export function NameComposite({
  legend,
  firstNameField,
  middleNameField,
  lastNameField,
  values,
  issuesByFieldId,
  onChange,
  touchedFieldIds,
  onBlur,
}: NameCompositeProps): JSX.Element {
  // Backward-compatible default: when the caller doesn't pass a
  // touched set, render each sub-field as touched (errors visible) so
  // pre-Phase-7b consumers behave identically.
  const isTouched = (fieldId: string): boolean =>
    touchedFieldIds === undefined || touchedFieldIds.has(fieldId);

  return (
    <fieldset className={styles.composite}>
      <legend className={styles.composite__legend}>{legend}</legend>
      <div className={styles.composite__row}>
        <TextInput
          field={firstNameField}
          value={values.first}
          issues={issuesByFieldId.get(firstNameField.id) ?? []}
          onChange={next => onChange('first', next)}
          touched={isTouched(firstNameField.id)}
          onBlur={onBlur ? () => onBlur('first') : undefined}
        />
        <TextInput
          field={middleNameField}
          value={values.middle}
          issues={issuesByFieldId.get(middleNameField.id) ?? []}
          onChange={next => onChange('middle', next)}
          touched={isTouched(middleNameField.id)}
          onBlur={onBlur ? () => onBlur('middle') : undefined}
        />
        <TextInput
          field={lastNameField}
          value={values.last}
          issues={issuesByFieldId.get(lastNameField.id) ?? []}
          onChange={next => onChange('last', next)}
          touched={isTouched(lastNameField.id)}
          onBlur={onBlur ? () => onBlur('last') : undefined}
        />
      </div>
    </fieldset>
  );
}
